import { ORG_COOKIE_NAME } from '@mls-wallet/shared-types';
import { getFrontendEnvVariables } from '@mls-wallet/env';

function getSharedCookieDomain(): string | null {
  const rawApiUrl = getFrontendEnvVariables().DEN_API_URL;

  try {
    const apiHost = new URL(rawApiUrl).hostname;
    const uiHost = window.location.hostname;

    // Prefer host-only cookies when the UI and API share the same host.
    if (apiHost === uiHost) return null;

    // Browsers reject Domain=localhost (and other single-label hosts).
    if (!uiHost.includes('.') || !apiHost.includes('.')) return null;

    const uiParts = uiHost.split('.');
    const apiParts = apiHost.split('.');
    const shared: string[] = [];

    for (let i = 1; i <= Math.min(uiParts.length, apiParts.length); i++) {
      const uiPart = uiParts.at(-i);
      const apiPart = apiParts.at(-i);
      if (!uiPart || !apiPart || uiPart !== apiPart) break;
      shared.unshift(uiPart);
    }

    // Require at least two labels (heuristic for a registrable domain).
    if (shared.length < 2) return null;

    return shared.join('.');
  } catch {
    return null;
  }
}

function getCookieAttributes(): string {
  const attrs: string[] = ['path=/', 'SameSite=Lax'];

  const domain = getSharedCookieDomain();
  if (domain) {
    attrs.push(`Domain=${domain}`);
  }

  // Only set Secure on HTTPS; Secure cookies are ignored on http://localhost.
  if (window.location.protocol === 'https:') {
    attrs.push('Secure');
  }

  return attrs.join('; ');
}

/**
 * Set the current organization ID cookie.
 * This cookie is sent with all API requests and used by the backend
 * to validate that the user is accessing the correct organization.
 *
 * @param organizationId - The organization ID to set, or null to clear
 */
export function setOrgCookie(organizationId: string | null): void {
  const attrs = getCookieAttributes();
  if (organizationId) {
    // Set cookie with path=/ so it's sent with all requests
    // SameSite=Lax for CSRF protection while allowing navigation
    document.cookie = `${ORG_COOKIE_NAME}=${organizationId}; ${attrs}`;
  } else {
    // Clear the cookie by setting it to expire in the past
    document.cookie = `${ORG_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; ${attrs}`;
  }
}
